import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { CommonModule } from '@angular/common';

import * as THREE from 'three';

import {
  OrbitControls
} from 'three/examples/jsm/controls/OrbitControls.js';
import {
  LineMaterial
} from 'three/examples/jsm/lines/LineMaterial.js';
import {
  VolumeViewerOptions,
  VolumeViewerSources,
  VolumeViewerStatus
} from './volume-viewer.models';

import {
  VolumeFileLoaderService
} from './volume-file-loader.service';

import {
  VolumeLasLoaderService
} from './las-loader.service';

import {
  VolumeDxfLoaderService
} from './dxf-loader.service';
import { TranslocoPipe } from '@jsverse/transloco';

const DEFAULT_OPTIONS: Required<VolumeViewerOptions> = {
  backgroundColor: 0x17191c,

  pointColor: 0xc6d7e8,
  pointSize: 0.05,

  lineColor: 0xffffff,
  lineWidth: 3,

  showGrid: true,
  showAxes: true,

  classificationColor1: 0x2a00a6, // TRIADIC_1 - violet
  classificationColor2: 0xfd0091, // TRIADIC_2 - rose/magenta
};

@Component({
  selector: 'volume-viewer',
  standalone: true,
  imports: [CommonModule, TranslocoPipe],
  templateUrl: './volume-viewer.component.html',
  styleUrls: ['./volume-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VolumeViewerComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('viewerContainer', { static: true })
  private viewerContainerRef!: ElementRef<HTMLDivElement>;

  @Input({ required: true })
  sources!: VolumeViewerSources;

  @Input()
  options: VolumeViewerOptions = {};

  @Input()
  showToolbar = true;

  @Input()
  classificationDate1: number | null = null;

  @Input()
  classificationDate2: number | null = null;

  @Output()
  readonly ready = new EventEmitter<void>();

  @Output()
  readonly loadError = new EventEmitter<Error>();

  status: VolumeViewerStatus = {
    state: 'idle',
    message: 'En attente des sources.'
  };

  pointCount = 0;
  dxfLineCount = 0;

  pointCloudVisible = true;
  dxfVisible = true;

  private scene?: THREE.Scene;
  private camera?: THREE.PerspectiveCamera;
  private renderer?: THREE.WebGLRenderer;
  private controls?: OrbitControls;

  private pointCloud?: THREE.Points;
  private dxfGroup?: THREE.Group;

  private grid?: THREE.GridHelper;
  private axes?: THREE.AxesHelper;

  private resizeObserver?: ResizeObserver;
  private animationFrameId?: number;
  private loadController?: AbortController;

  private viewInitialized = false;
  private loadGeneration = 0;

  constructor(
    private readonly fileLoader: VolumeFileLoaderService,
    private readonly lasLoader: VolumeLasLoaderService,
    private readonly dxfLoader: VolumeDxfLoaderService,
    private readonly zone: NgZone,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.initializeViewer();
    this.viewInitialized = true;

    if (this.hasValidSources()) {
      void this.reload();
    }
  }

  ngOnChanges(
    changes: SimpleChanges
  ): void {
    if (!this.viewInitialized) {
      return;
    }

    if (changes['options']) {
      this.applyOptions();
    }

    if (
      changes['sources'] &&
      this.hasValidSources()
    ) {
      void this.reload();
    }
  }

  ngOnDestroy(): void {
    this.loadController?.abort();
    this.resizeObserver?.disconnect();

    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.controls?.dispose();

    this.removePointCloud();
    this.removeDxfGroup();

    if (this.grid) {
      this.scene?.remove(this.grid);
      this.disposeObject(this.grid);
    }

    if (this.axes) {
      this.scene?.remove(this.axes);
      this.disposeObject(this.axes);
    }

    this.renderer?.renderLists.dispose();
    this.renderer?.dispose();
    this.renderer?.forceContextLoss();
    this.renderer?.domElement.remove();

    this.scene?.clear();
  }

  async reload(): Promise<void> {
    if (!this.hasValidSources()) {
      this.setStatus(
        'error',
        'Les URLs LAS et DXF sont obligatoires.'
      );

      return;
    }

    this.loadController?.abort();
    this.loadController = new AbortController();

    const signal = this.loadController.signal;
    const generation = ++this.loadGeneration;

    this.removePointCloud();
    this.removeDxfGroup();

    this.pointCount = 0;
    this.dxfLineCount = 0;

    this.setStatus(
      'loading',
      'volume_viewer_loading_files'
    );
    try {
      const [lasBuffer, dxfText] = await Promise.all([
        this.fileLoader.loadArrayBuffer(
          this.sources.lasUrl,
          signal,
          'LAS/LAZ'
        ),
        this.fileLoader.loadText(
          this.sources.dxfUrl,
          signal,
          'DXF'
        )
      ]);

      this.assertCurrentLoad(
        generation,
        signal
      );

      const options = this.getOptions();

      this.setStatus(
        'parsing',
        'Analyse du nuage de points…'
      );

      const lasResult =
        await this.lasLoader.load(
          lasBuffer,
          {
            pointSize:
            options.pointSize,

            pointColor:
            options.pointColor,

            classificationColor1:
            options.classificationColor1,

            classificationColor2:
            options.classificationColor2
          }
        );

      this.assertCurrentLoad(
        generation,
        signal
      );

      this.setStatus(
        'parsing',
        'Analyse du DXF…'
      );

      const dxfResult =
        this.dxfLoader.load(
          dxfText,
          lasResult.origin,
          {
            lineColor: options.lineColor,
            lineWidth: options.lineWidth
          }
        );

      this.assertCurrentLoad(
        generation,
        signal
      );

      this.pointCloud =
        lasResult.pointCloud;

      this.dxfGroup =
        dxfResult.group;

      this.pointCloud.visible =
        this.pointCloudVisible;

      this.dxfGroup.visible =
        this.dxfVisible;

      this.scene?.add(this.pointCloud);
      this.scene?.add(this.dxfGroup);

      this.pointCount =
        lasResult.pointCount;

      this.dxfLineCount =
        dxfResult.lineCount;

      this.fitCamera();

      this.setStatus(
        'ready',
        'Visualisation prête.'
      );

      this.ready.emit();
    } catch (error: unknown) {
      if (this.isAbortError(error)) {
        return;
      }

      const normalizedError =
        error instanceof Error
          ? error
          : new Error(
            'Une erreur inconnue est survenue.'
          );

      console.error(
        'Erreur VolumeViewer :',
        normalizedError
      );

      this.setStatus(
        'error',
        normalizedError.message
      );

      this.loadError.emit(
        normalizedError
      );
    }
  }

  fitCamera(): void {
    if (!this.camera || !this.controls) {
      return;
    }

    const objects: THREE.Object3D[] = [];

    if (this.pointCloud?.visible) {
      objects.push(this.pointCloud);
    }

    if (
      this.dxfGroup?.visible &&
      this.dxfGroup.children.length > 0
    ) {
      objects.push(this.dxfGroup);
    }

    if (objects.length === 0) {
      return;
    }

    const bounds = new THREE.Box3();

    for (const object of objects) {
      bounds.expandByObject(
        object,
        true
      );
    }

    if (bounds.isEmpty()) {
      return;
    }

    const center = bounds.getCenter(
      new THREE.Vector3()
    );

    const size = bounds.getSize(
      new THREE.Vector3()
    );

    const maximumDimension = Math.max(
      size.x,
      size.y,
      size.z,
      1
    );

    const verticalFov =
      THREE.MathUtils.degToRad(
        this.camera.fov
      );

    const distance =
      maximumDimension /
      (2 * Math.tan(verticalFov / 2));

    const direction =
      new THREE.Vector3(
        1,
        -1,
        0.8
      ).normalize();

    this.camera.position.copy(
      center
        .clone()
        .addScaledVector(
          direction,
          distance * 1.8
        )
    );

    this.camera.near = Math.max(
      maximumDimension / 100_000,
      0.001
    );

    this.camera.far = Math.max(
      maximumDimension * 1_000,
      distance * 20,
      10_000
    );

    this.camera.updateProjectionMatrix();

    this.controls.target.copy(center);
    this.controls.update();
  }

  togglePointCloud(): void {
    this.pointCloudVisible =
      !this.pointCloudVisible;

    if (this.pointCloud) {
      this.pointCloud.visible =
        this.pointCloudVisible;
    }
  }

  toggleDxf(): void {
    this.dxfVisible =
      !this.dxfVisible;

    if (this.dxfGroup) {
      this.dxfGroup.visible =
        this.dxfVisible;
    }
  }

  private initializeViewer(): void {
    const container =
      this.viewerContainerRef.nativeElement;

    const options = this.getOptions();

    this.scene = new THREE.Scene();

    this.scene.background =
      new THREE.Color(
        options.backgroundColor
      );

    this.camera =
      new THREE.PerspectiveCamera(
        50,
        this.getContainerAspect(),
        0.01,
        1_000_000
      );

    this.camera.up.set(0, 0, 1);
    this.camera.position.set(
      10,
      -10,
      10
    );

    this.renderer =
      new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: 'high-performance'
      });

    this.renderer.setPixelRatio(
      Math.min(
        window.devicePixelRatio,
        2
      )
    );

    this.renderer.setSize(
      Math.max(container.clientWidth, 1),
      Math.max(container.clientHeight, 1),
      false
    );

    container.appendChild(
      this.renderer.domElement
    );

    this.controls =
      new OrbitControls(
        this.camera,
        this.renderer.domElement
      );

    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.screenSpacePanning = true;

    this.createHelpers();

    this.resizeObserver =
      new ResizeObserver(
        () => this.resizeViewer()
      );

    this.resizeObserver.observe(
      container
    );

    this.zone.runOutsideAngular(() => {
      this.animate();
    });
  }

  private createHelpers(): void {
    const options = this.getOptions();

    if (options.showGrid) {
      this.grid = new THREE.GridHelper(
        100,
        20
      );

      this.grid.rotateX(
        Math.PI / 2
      );

      this.scene?.add(this.grid);
    }

    if (options.showAxes) {
      this.axes =
        new THREE.AxesHelper(10);

      this.scene?.add(this.axes);
    }
  }

  private applyOptions(): void {
    const options = this.getOptions();

    /*
     * Background.
     */
    if (this.scene) {
      this.scene.background =
        new THREE.Color(
          options.backgroundColor
        );
    }

    /*
     * Nuage de points.
     */
    const pointMaterial =
      this.pointCloud?.material;

    if (
      pointMaterial instanceof
      THREE.PointsMaterial
    ) {
      pointMaterial.size =
        options.pointSize;

      pointMaterial.needsUpdate = true;
    }

    /*
     * Recoloration des classifications
     * sans recharger/reparser le LAZ.
     */
    if (this.pointCloud) {
      this.lasLoader.updateClassificationColors(
        this.pointCloud,
        options.classificationColor1,
        options.classificationColor2,
        options.pointColor
      );
    }

    /*
     * DXF :
     * modification directe des LineMaterial.
     */
    this.dxfGroup?.traverse(object => {

      const material =
        (object as THREE.Object3D & {
          material?: THREE.Material;
        }).material;

      if (!(material instanceof LineMaterial)) {
        return;
      }

      material.color.setHex(
        options.lineColor
      );

      material.linewidth =
        options.lineWidth;

      material.needsUpdate = true;
    });

    if (this.grid) {
      this.grid.visible =
        options.showGrid;
    }

    if (this.axes) {
      this.axes.visible =
        options.showAxes;
    }
  }

  private resizeViewer(): void {
    if (!this.renderer || !this.camera) {
      return;
    }

    const container =
      this.viewerContainerRef.nativeElement;

    const width = Math.max(
      container.clientWidth,
      1
    );

    const height = Math.max(
      container.clientHeight,
      1
    );

    this.camera.aspect =
      width / height;

    this.camera.updateProjectionMatrix();

    this.renderer.setSize(
      width,
      height,
      false
    );
  }

  private animate = (): void => {
    this.animationFrameId =
      requestAnimationFrame(
        this.animate
      );

    this.controls?.update();

    if (
      this.renderer &&
      this.scene &&
      this.camera
    ) {
      this.renderer.render(
        this.scene,
        this.camera
      );
    }
  };

  private removePointCloud(): void {
    if (!this.pointCloud) {
      return;
    }

    this.scene?.remove(
      this.pointCloud
    );

    this.disposeObject(
      this.pointCloud
    );

    this.pointCloud = undefined;
  }

  private removeDxfGroup(): void {
    if (!this.dxfGroup) {
      return;
    }

    this.scene?.remove(
      this.dxfGroup
    );

    this.disposeObject(
      this.dxfGroup
    );

    this.dxfGroup = undefined;
  }

  private disposeObject(
    object: THREE.Object3D
  ): void {
    object.traverse(child => {
      const renderable = child as {
        geometry?: THREE.BufferGeometry;
        material?:
          | THREE.Material
          | THREE.Material[];
      };

      renderable.geometry?.dispose();

      if (
        Array.isArray(
          renderable.material
        )
      ) {
        for (
          const material of
          renderable.material
          ) {
          material.dispose();
        }
      } else {
        renderable.material?.dispose();
      }
    });
  }

  private getContainerAspect(): number {
    const container =
      this.viewerContainerRef.nativeElement;

    return (
      Math.max(
        container.clientWidth,
        1
      ) /
      Math.max(
        container.clientHeight,
        1
      )
    );
  }

  private getOptions(): Required<VolumeViewerOptions> {
    return {
      ...DEFAULT_OPTIONS,
      ...this.options
    };
  }

  private hasValidSources(): boolean {
    return Boolean(
      this.sources?.lasUrl?.trim() &&
      this.sources?.dxfUrl?.trim()
    );
  }

  private assertCurrentLoad(
    generation: number,
    signal: AbortSignal
  ): void {
    if (
      signal.aborted ||
      generation !== this.loadGeneration
    ) {
      throw new DOMException(
        'Chargement annulé.',
        'AbortError'
      );
    }
  }

  private isAbortError(
    error: unknown
  ): boolean {
    return (
      error instanceof DOMException &&
      error.name === 'AbortError'
    );
  }

  private setStatus(
    state: VolumeViewerStatus['state'],
    message: string
  ): void {
    this.status = {
      state,
      message
    };

    this.changeDetector.markForCheck();
  }

  classificationColor1Hex =
    this.numberToHex(
      this.getOptions().classificationColor1
    );

  classificationColor2Hex =
    this.numberToHex(
      this.getOptions().classificationColor2
    );

  lineColorHex =
    this.numberToHex(
      this.getOptions().lineColor
    );

  lineWidth =
    this.getOptions().lineWidth;

  onClassificationColor1Change(
    event: Event
  ): void {

    const input =
      event.target as HTMLInputElement;

    this.classificationColor1Hex =
      input.value;

    this.options = {
      ...this.options,
      classificationColor1:
        this.hexToNumber(input.value)
    };

    this.applyOptions();
  }

  onClassificationColor2Change(
    event: Event
  ): void {

    const input =
      event.target as HTMLInputElement;

    this.classificationColor2Hex =
      input.value;

    this.options = {
      ...this.options,
      classificationColor2:
        this.hexToNumber(input.value)
    };

    this.applyOptions();
  }

  onLineColorChange(
    event: Event
  ): void {

    const input =
      event.target as HTMLInputElement;

    this.lineColorHex =
      input.value;

    this.options = {
      ...this.options,
      lineColor:
        this.hexToNumber(input.value)
    };

    this.applyOptions();
  }

  onLineWidthChange(
    event: Event
  ): void {

    const input =
      event.target as HTMLInputElement;

    const value =
      Number(input.value);

    if (!Number.isFinite(value)) {
      return;
    }

    this.lineWidth = value;

    this.options = {
      ...this.options,
      lineWidth: value
    };

    this.applyOptions();
  }

  private numberToHex(
    value: number
  ): string {

    return (
      '#' +
      value
        .toString(16)
        .padStart(6, '0')
        .slice(-6)
    );
  }

  private hexToNumber(
    value: string
  ): number {

    return Number.parseInt(
      value.replace('#', ''),
      16
    );
  }
}
